// ---------------------------------------------------------------------------
// Setup guides - the owner's lane, in the order the plan says to do it.
//
// These are do-it-live. Every step points at the real control on the real page
// and waits for the real save, so when a guide ends the thing is actually
// configured. Nothing here simulates anything, and nothing writes on the user's
// behalf except an explicit "do it for me" on a step that only navigates.
//
// Each guide carries a `readinessKey` matching an item in the backend readiness
// map, which is what lets the dashboard card and the launcher say "done" without
// asking whether the guide itself was completed. Someone who set their working
// hours by hand a year ago should never be told they still have to.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const CAMPUS_PROFILE = PATH_ADMIN.settings.tab('general-settings', 'general-info');
const WORKING_HOURS = PATH_ADMIN.settings.tab('general-settings', 'working-hours');
const BILLING_CENTERS = PATH_ADMIN.settings.tab('invoicing', 'billing-centers');
const TAX_CONFIG = PATH_ADMIN.settings.tab('invoicing', 'tax-configuration');
const INVOICE_NUMBERING = PATH_ADMIN.settings.tab('invoicing', 'invoice-id');
const INVOICE_TEMPLATES = PATH_ADMIN.settings.tab('invoicing', 'invoice-templates');
const BRANDING = PATH_ADMIN.settings.tab('general-settings', 'org-settings');
const MEETING_ROOMS = PATH_ADMIN.meetingRoomManagement.root;

export const SETUP_CAMPUS_PROFILE = {
  id: 'setup-campus-profile',
  title: 'Set up your campus profile',
  summary:
    'Your name, address and contact details. They appear on invoices, proposals '
    + 'and everything your members see.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 3,
  readinessKey: 'campus_profile',
  keywords: ['campus', 'profile', 'address', 'contact', 'general information', 'settings'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: CAMPUS_PROFILE,
      target: CENTER,
      placement: 'center',
      title: 'Start with who you are',
      body:
        'This page is the source of truth for your campus. The address here is the '
        + 'one printed on invoices, the phone number is the one on your proposals, '
        + 'and the name is what members see when they log in. Worth getting right '
        + 'once.',
    },
    {
      id: 'edit',
      kind: 'act',
      route: CAMPUS_PROFILE,
      target: ANCHOR.settingsCampusEdit,
      placement: 'bottom',
      title: 'Switch to edit mode',
      body: 'The page opens read-only so nobody changes your address by leaning on a key.',
      waitingLabel: 'Click Edit.',
      advanceOn: [{ type: 'click', target: ANCHOR.settingsCampusEdit }],
      doItForMe: { type: 'click', target: ANCHOR.settingsCampusEdit },
      allowSkip: true,
    },
    {
      id: 'fill-and-save',
      kind: 'await',
      route: CAMPUS_PROFILE,
      target: ANCHOR.settingsCampusEdit,
      placement: 'bottom',
      title: 'Fill it in, then save',
      body:
        'Campus name, the full postal address, and a phone number and email your '
        + 'members can actually reach. The website and social links are optional. '
        + 'Hit Save when you are happy and I will pick things up from there.',
      waitingLabel: 'Waiting for you to save.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.campusProfileSaved }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: CAMPUS_PROFILE,
      target: CENTER,
      placement: 'center',
      title: 'That is your profile set',
      body:
        'Everything downstream now has an address to print. Next in the setup lane '
        + 'is your working hours, which is what check-ins, bookings and day passes '
        + 'all measure themselves against.',
    },
  ],
};

export const SETUP_WORKING_HOURS = {
  id: 'setup-working-hours',
  title: 'Set your working hours',
  summary:
    'The daily open and close times for your campus, and which days you are shut. '
    + 'Check-ins, room bookings and day passes all follow them.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 3,
  readinessKey: 'working_hours',
  keywords: ['working hours', 'opening', 'closing', 'timings', 'schedule', 'closed', 'weekend'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: WORKING_HOURS,
      target: CENTER,
      placement: 'center',
      title: 'When are you open?',
      body:
        'This is not decoration. Meeting rooms will not offer slots outside these '
        + 'hours, day passes are priced against them, and your check-in reports use '
        + 'them to decide what counts as early or late.',
    },
    {
      id: 'edit',
      kind: 'act',
      route: WORKING_HOURS,
      target: ANCHOR.settingsWorkingHoursEdit,
      placement: 'left',
      title: 'Open the editor',
      body: 'Same pattern as the rest of settings: read-only until you ask to edit.',
      waitingLabel: 'Click the pencil.',
      advanceOn: [{ type: 'click', target: ANCHOR.settingsWorkingHoursEdit }],
      doItForMe: { type: 'click', target: ANCHOR.settingsWorkingHoursEdit },
      allowSkip: true,
    },
    {
      id: 'set-days',
      kind: 'await',
      route: WORKING_HOURS,
      target: ANCHOR.settingsWorkingHoursEdit,
      placement: 'left',
      title: 'Set each day, then save',
      body:
        'Toggle a day off if you are closed, and set opening and closing times for '
        + 'the rest. Times are 24-hour. If you run a 24-hour space, look at Shifts '
        + 'in the same settings section instead. Save with the same button when you '
        + 'are done.',
      waitingLabel: 'Waiting for you to save.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.workingHoursSaved }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: WORKING_HOURS,
      target: CENTER,
      placement: 'center',
      title: 'Hours are live',
      body:
        'Holidays are separate, under General Settings, for the days you close on '
        + 'top of your normal week. Next up is your spaces and seats, which is the '
        + 'inventory everything else is sold from.',
    },
  ],
};

export const SETUP_SPACES_SEATS = {
  id: 'setup-spaces-seats',
  title: 'Create your first space and seats',
  summary:
    'Spaces are the rooms and zones you sell. Seats are the individual desks '
    + 'inside them. Nothing can be sold until these exist.',
  category: 'setup',
  level: 'advanced',
  permission: ['spaces.view', 'spaces.manage'],
  featureSlug: null,
  estMinutes: 6,
  readinessKey: 'spaces_seats',
  keywords: ['space', 'seats', 'desk', 'cabin', 'inventory', 'add space', 'private office'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PATH_ADMIN.SpaceManagement,
      target: CENTER,
      placement: 'center',
      title: 'Spaces are your inventory',
      body:
        'A space is a thing you can sell: a private cabin, a hot-desk zone, a '
        + 'dedicated-desk area. Each one holds a number of seats, and a seat is what '
        + 'a member actually occupies. Plans price them, subscriptions reserve them, '
        + 'and the live board colours them. So this is the first real thing to build.',
    },
    {
      id: 'open-form',
      kind: 'act',
      route: PATH_ADMIN.SpaceManagement,
      target: ANCHOR.spacesAddNew,
      placement: 'bottom',
      title: 'Add your first space',
      body:
        'Start with something simple, like one open-desk area. You can add cabins '
        + 'and meeting-room-adjacent zones after.',
      waitingLabel: 'Click Add New Space.',
      advanceOn: [{ type: 'click', target: ANCHOR.spacesAddNew }],
      doItForMe: { type: 'click', target: ANCHOR.spacesAddNew },
      allowSkip: true,
    },
    {
      id: 'describe',
      kind: 'narrate',
      route: PATH_ADMIN.addSpace,
      target: CENTER,
      placement: 'center',
      title: 'The three fields that matter',
      body:
        'Name is what your team and members see. Space type decides how it behaves, '
        + 'so a private cabin is sold whole while an open area is sold seat by seat. '
        + 'Total seats is the count DeskOS will track occupancy against, so make it '
        + 'the real number of desks, not the number you hope to sell.',
    },
    {
      id: 'publish',
      kind: 'await',
      route: PATH_ADMIN.addSpace,
      target: ANCHOR.spacePublish,
      placement: 'bottom',
      title: 'Publish it',
      body:
        'Seats are generated for you from the seat count, numbered in order. If you '
        + 'want some of them held back from day passes or walk-ins, tag them in the '
        + 'seat table before you publish. Nothing here is final, you can edit the '
        + 'space and add seats later.',
      waitingLabel: 'Waiting for the space to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.spaceCreated }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: PATH_ADMIN.SpaceManagement,
      target: CENTER,
      placement: 'center',
      title: 'You have inventory',
      body:
        'Every seat you just created is vacant and sellable. Live Status will show '
        + 'them from now on. Next is pricing: a plan is what turns a seat into an '
        + 'amount on an invoice.',
    },
  ],
};

// The plans page opens on the Monthly tab by its slug. The editor's address
// carries ?type=monthly, but the step matches on the path alone so a plan
// started from any tab still counts.
const PLANS_MONTHLY_TAB = `${PATH_ADMIN.plans.root}?tab=monthly`;

export const SETUP_PLANS = {
  id: 'setup-plans',
  title: 'Create a monthly plan',
  summary:
    'A plan is the price a seat sells at, plus what comes with it. Subscriptions '
    + 'and proposals both start from one.',
  category: 'setup',
  level: 'advanced',
  permission: ['plans.view', 'plans.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: 'plans',
  keywords: ['plan', 'pricing', 'monthly plan', 'price', 'rate card', 'per seat'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PLANS_MONTHLY_TAB,
      target: CENTER,
      placement: 'center',
      title: 'Plans are your rate card',
      body:
        'A monthly plan says what a seat costs per month, which spaces it applies '
        + 'to, and what is bundled with it, like meeting-room credits. When you sell '
        + 'to a company you pick a plan, choose the seats, and the invoice writes '
        + 'itself. You can always override the price on the individual deal.',
    },
    {
      id: 'open-form',
      kind: 'act',
      route: PLANS_MONTHLY_TAB,
      target: ANCHOR.plansCreateNew,
      placement: 'bottom',
      title: 'Create a plan',
      body: 'One plan per price point. Most operators start with two or three.',
      waitingLabel: 'Click New monthly.',
      advanceOn: [{ type: 'click', target: ANCHOR.plansCreateNew }],
      doItForMe: { type: 'click', target: ANCHOR.plansCreateNew },
      allowSkip: true,
    },
    {
      id: 'describe',
      kind: 'narrate',
      route: PATH_ADMIN.plans.new(),
      target: CENTER,
      placement: 'center',
      title: 'Price it, then attach the spaces',
      body:
        'Per-seat price is charged monthly for each seat on the subscription. '
        + 'Minimum seats stops a plan meant for teams being bought for one desk. '
        + 'The spaces you tick are the ones this price is valid in, so a premium '
        + 'cabin rate should not be attached to your open area.',
    },
    {
      id: 'save',
      kind: 'await',
      route: PATH_ADMIN.plans.new(),
      target: ANCHOR.planSubmit,
      placement: 'bottom',
      title: 'Create it',
      body:
        'Taxes are not part of the plan price. They come from your tax '
        + 'configuration at invoice time, so enter the amount you want to charge '
        + 'before tax.',
      waitingLabel: 'Waiting for the plan to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.planCreated }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: PLANS_MONTHLY_TAB,
      target: CENTER,
      placement: 'center',
      title: 'You can sell now',
      body:
        'Seats plus a price is enough to create a real subscription. What is left '
        + 'is getting the money side right: the billing centre that legally issues '
        + 'the invoice, your tax setup, and the invoice numbering. Those are the '
        + 'next three guides.',
    },
  ],
};

export const SETUP_BILLING_CENTER = {
  id: 'setup-billing-center',
  title: 'Set up your billing centre',
  summary:
    'The legal entity that issues your invoices. Its name, address and tax id '
    + 'are what appear on every bill you send.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 5,
  readinessKey: 'billing_center',
  keywords: ['billing center', 'billing centre', 'entity', 'company', 'gstin', 'legal', 'invoice from'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: BILLING_CENTERS,
      target: CENTER,
      placement: 'center',
      title: 'Who is actually billing?',
      body:
        'A campus is a place. A billing centre is a company. The billing centre is '
        + 'the entity whose name, address and tax id go on the invoice, and it is '
        + 'what your invoice numbering, tax setup, payment gateway and invoice '
        + 'template all hang off. One entity can bill for several campuses, and one '
        + 'campus can have more than one entity if you run separate books.',
    },
    {
      id: 'open-dialog',
      kind: 'act',
      route: BILLING_CENTERS,
      target: ANCHOR.settingsBillingCenterAdd,
      placement: 'bottom',
      title: 'Add your entity',
      body: 'Start with the company you invoice from today. You can add more later.',
      waitingLabel: 'Click Add Billing Center.',
      advanceOn: [{ type: 'click', target: ANCHOR.settingsBillingCenterAdd }],
      doItForMe: { type: 'click', target: ANCHOR.settingsBillingCenterAdd },
      allowSkip: true,
    },
    {
      id: 'fill-and-save',
      kind: 'await',
      route: BILLING_CENTERS,
      target: CENTER,
      placement: 'center',
      title: 'Legal name, address, tax id',
      body:
        'Use the registered name and address, not the trading name, because this is '
        + 'the version that has to stand up on a tax invoice. Tick the campuses this '
        + 'entity is allowed to bill for, and mark it default for the ones it should '
        + 'bill by default. Then Create.',
      waitingLabel: 'Waiting for the billing centre to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.billingCenterCreated }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: BILLING_CENTERS,
      target: CENTER,
      placement: 'center',
      title: 'Everything money-related now has an owner',
      body:
        'From here on, tax and invoice numbering are configured per billing centre, '
        + 'not per campus. If you add a second entity later, remember it needs its '
        + 'own tax config and its own number series.',
    },
  ],
};

export const SETUP_TAX = {
  id: 'setup-tax',
  title: 'Configure tax',
  summary:
    'The rates DeskOS adds to invoices, and the rules for when each one applies.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 4,
  readinessKey: 'tax_config',
  keywords: ['tax', 'gst', 'cgst', 'sgst', 'igst', 'vat', 'sales tax', 'rate'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: TAX_CONFIG,
      target: CENTER,
      placement: 'center',
      title: 'Tax belongs to the entity',
      body:
        'Pick the billing centre at the top, because this configuration belongs to '
        + 'it, not to the campus. Plan prices you enter elsewhere are always '
        + 'pre-tax. What you set here is added on top at invoice time.',
    },
    {
      id: 'preset',
      kind: 'act',
      route: TAX_CONFIG,
      target: ANCHOR.settingsTaxPresets,
      placement: 'top',
      title: 'Start from a preset',
      body:
        'India gives you CGST, SGST and IGST at the standard rate, with the '
        + 'same-state and different-state rules already wired. Other countries get a '
        + 'single rate you can rename and adjust. A preset replaces whatever '
        + 'components are there now, so apply it before you hand-edit anything.',
      waitingLabel: 'Pick a preset, or add a component by hand.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.taxConfigSaved }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: TAX_CONFIG,
      target: CENTER,
      placement: 'center',
      title: 'Rates are live',
      body:
        'Every invoice raised for this entity now carries these components. If a '
        + 'particular customer is exempt, that is set on the customer, not here. '
        + 'And if you add a second billing centre, it starts with no tax config of '
        + 'its own, so come back and do this again for it.',
    },
  ],
};

export const SETUP_INVOICE_NUMBERING = {
  id: 'setup-invoice-numbering',
  title: 'Set up invoice numbering',
  summary:
    'The format and running sequence your invoice numbers follow. Worth getting '
    + 'right before you issue the first one.',
  category: 'setup',
  level: 'advanced',
  // The numbering builder lives inside the settings page, so the code that
  // opens the page is needed as well as the one that owns the series.
  permission: ['settings.manage', 'billing.numbering'],
  featureSlug: null,
  estMinutes: 4,
  readinessKey: 'invoice_numbering',
  keywords: ['invoice number', 'numbering', 'series', 'prefix', 'sequence', 'invoice id'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: INVOICE_NUMBERING,
      target: CENTER,
      placement: 'center',
      title: 'Numbers are permanent',
      body:
        'A series is per billing centre, and once a number is issued it is spent. '
        + 'That is deliberate: an audit wants a clean unbroken sequence. So decide '
        + 'the format now rather than after fifty invoices are out.',
    },
    {
      id: 'create',
      kind: 'act',
      route: INVOICE_NUMBERING,
      target: ANCHOR.settingsInvoiceSeriesCreate,
      placement: 'bottom',
      title: 'Create a series',
      body:
        'Most operators use a prefix, the financial year and a running counter, so '
        + 'something like INV/26-27/0001. The builder shows a live preview as you '
        + 'add components.',
      waitingLabel: 'Click Create New Series.',
      advanceOn: [{ type: 'click', target: ANCHOR.settingsInvoiceSeriesCreate }],
      doItForMe: { type: 'click', target: ANCHOR.settingsInvoiceSeriesCreate },
      allowSkip: true,
    },
    {
      id: 'save',
      kind: 'await',
      route: INVOICE_NUMBERING,
      target: CENTER,
      placement: 'center',
      title: 'Build the pattern, then save',
      body:
        'Set the starting number to continue from wherever your old system stopped, '
        + 'not from 1, if you are migrating mid-year. Mark it the default series so '
        + 'invoices pick it up without anyone choosing.',
      waitingLabel: 'Waiting for the series to be saved.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.invoiceNumberingSaved }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: INVOICE_NUMBERING,
      target: CENTER,
      placement: 'center',
      title: 'Numbering is set',
      body:
        'Proformas and credit notes can have their own series if you want them kept '
        + 'apart from tax invoices. The last piece of the money setup is the invoice '
        + 'template, which is the letterhead this entity prints on.',
    },
  ],
};

export const SETUP_INVOICE_TEMPLATE = {
  id: 'setup-invoice-template',
  title: 'Design your invoice template',
  summary:
    'The letterhead your invoices print on: logo, terms, bank details and the '
    + 'wording at the bottom.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 5,
  readinessKey: 'invoice_template',
  keywords: ['invoice template', 'letterhead', 'logo', 'terms', 'bank details', 'pdf'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: INVOICE_TEMPLATES,
      target: CENTER,
      placement: 'center',
      title: 'This is your letterhead',
      body:
        'A template belongs to a billing centre, not a campus, because it carries '
        + 'that entity\'s logo, bank details and terms. Whichever template is marked '
        + 'default for the entity is the one an invoice prints on, so most operators '
        + 'need exactly one per entity.',
    },
    {
      id: 'create',
      kind: 'act',
      route: INVOICE_TEMPLATES,
      target: ANCHOR.settingsInvoiceTemplateCreate,
      placement: 'bottom',
      title: 'Start from the stock template',
      body:
        'Creating a new one copies the existing layout, so you are editing rather '
        + 'than starting from a blank page. If a template already exists, you can '
        + 'open it and edit it instead. Either counts.',
      waitingLabel: 'Open a template, or create one.',
      advanceOn: [{ type: 'click', target: ANCHOR.settingsInvoiceTemplateCreate }],
      doItForMe: { type: 'click', target: ANCHOR.settingsInvoiceTemplateCreate },
      allowSkip: true,
    },
    {
      id: 'save',
      kind: 'await',
      route: INVOICE_TEMPLATES,
      target: CENTER,
      placement: 'center',
      title: 'Fill in the parts that are yours',
      body:
        'Logo, bank account and payment instructions, and the terms line. Pick the '
        + 'billing centre it belongs to and mark it default. Preview renders it with '
        + 'real campus data and sample customer data, which is the fastest way to '
        + 'spot a missing field. Save when it reads right.',
      waitingLabel: 'Waiting for the template to be saved.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.invoiceTemplateSaved }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: INVOICE_TEMPLATES,
      target: CENTER,
      placement: 'center',
      title: 'The money setup is complete',
      body:
        'Entity, tax, numbering and letterhead. Every invoice DeskOS raises from now '
        + 'on has all four. Next is people: getting your team in, and giving them the '
        + 'right level of access.',
    },
  ],
};

export const SETUP_TEAM = {
  id: 'setup-team',
  title: 'Add your team',
  summary:
    'Get the people who run the space their own logins, with access to the '
    + 'campuses they actually work at.',
  category: 'setup',
  level: 'advanced',
  permission: ['team.view', 'team.manage'],
  featureSlug: null,
  estMinutes: 4,
  readinessKey: 'team',
  keywords: ['team', 'staff', 'employee', 'add member', 'colleague', 'login'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PATH_ADMIN.teamMembers,
      target: CENTER,
      placement: 'center',
      title: 'Your team, not your members',
      body:
        'This page is for people who work for you. The people who rent desks live '
        + 'under Memberships and never appear here. Everyone you add gets their own '
        + 'login, so nobody has to share yours, and every action they take is '
        + 'attributed to them.',
    },
    {
      id: 'open-dialog',
      kind: 'act',
      route: PATH_ADMIN.teamMembers,
      target: ANCHOR.teamAddMember,
      placement: 'bottom',
      title: 'Add someone',
      body: 'Start with whoever sits at the front desk. They use the system most.',
      waitingLabel: 'Click Add Member.',
      advanceOn: [{ type: 'click', target: ANCHOR.teamAddMember }],
      doItForMe: { type: 'click', target: ANCHOR.teamAddMember },
      allowSkip: true,
    },
    {
      id: 'save',
      kind: 'await',
      route: PATH_ADMIN.teamMembers,
      target: CENTER,
      placement: 'center',
      title: 'Name, email, role, password',
      body:
        'The role you pick decides what they can see. If none of the built-in roles '
        + 'fit, leave it for now and build a custom one in the next guide, then come '
        + 'back and assign it. They can change the password themselves after the '
        + 'first login.',
      waitingLabel: 'Waiting for the member to be added.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.teamMemberInvited }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: PATH_ADMIN.teamMembers,
      target: CENTER,
      placement: 'center',
      title: 'They can log in now',
      body:
        'Campus access is separate from role, so somebody with a manager role only '
        + 'manages the campuses you tick for them. Use the campus button on their row '
        + 'to change that. When someone leaves, offboard them rather than deleting '
        + 'the account, so their history stays intact.',
    },
  ],
};

export const SETUP_ROLES = {
  id: 'setup-roles',
  title: 'Build a custom role',
  summary:
    'Decide exactly what a job can see and do, by ticking permissions rather '
    + 'than picking a preset.',
  category: 'setup',
  level: 'advanced',
  permission: 'roles.manage',
  featureSlug: null,
  estMinutes: 5,
  readinessKey: 'roles',
  keywords: ['role', 'permission', 'access', 'restrict', 'custom role', 'who can see'],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PATH_ADMIN.settings.roles,
      target: CENTER,
      placement: 'center',
      title: 'Permissions, not job titles',
      body:
        'Access in DeskOS is a list of permission codes, and a role is just a named '
        + 'bundle of them. That is why a custom role can be as narrow as you like: '
        + 'a front-desk role that handles visitors and check-ins but never sees a '
        + 'single rupee of revenue is a normal thing to build.',
    },
    {
      id: 'create',
      kind: 'act',
      route: PATH_ADMIN.settings.roles,
      target: ANCHOR.rolesNewRole,
      placement: 'bottom',
      title: 'Create a role',
      body: 'Name it after the job, not the person. Roles outlive employees.',
      waitingLabel: 'Click New Role.',
      advanceOn: [{ type: 'click', target: ANCHOR.rolesNewRole }],
      doItForMe: { type: 'click', target: ANCHOR.rolesNewRole },
      allowSkip: true,
    },
    {
      id: 'save',
      kind: 'await',
      route: PATH_ADMIN.settings.roles,
      target: CENTER,
      placement: 'center',
      title: 'Tick what the job needs',
      body:
        'Permissions come in view and manage pairs, so give view where someone only '
        + 'needs to look. Financial figures sit behind their own permission, and '
        + 'without it revenue and invoice amounts are hidden rather than the page '
        + 'being blocked. Save when the list matches the job.',
      waitingLabel: 'Waiting for the role to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.customRoleCreated }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: PATH_ADMIN.settings.roles,
      target: CENTER,
      placement: 'center',
      title: 'Assign it to people',
      body:
        'A role does nothing until someone holds it. Go back to Team Members and '
        + 'assign it from the row menu. Changing a role updates everyone who holds '
        + 'it, straight away, so edit with that in mind.',
    },
  ],
};

export const SETUP_MEETING_ROOMS = {
  id: 'setup-meeting-rooms',
  title: 'Set up a meeting room',
  summary:
    'Add a bookable room, decide what an hour of it costs, and let members book '
    + 'it themselves instead of asking your front desk.',
  category: 'setup',
  level: 'advanced',
  // Three codes, because the guide crosses three gates: the list page, the
  // room it creates, and the create form itself, which sits behind the
  // settings code rather than the manage one.
  permission: ['meeting_rooms.view', 'meeting_rooms.manage', 'meeting_rooms.settings'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: 'meeting_rooms',
  keywords: [
    'meeting room', 'conference room', 'booking', 'credits', 'board room',
    'room booking', 'hourly',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: CENTER,
      placement: 'center',
      title: 'Rooms are sold by the hour, not the month',
      body:
        'A meeting room is not a space with seats. It is a resource people book '
        + 'in slots, priced per hour or paid for with the credits their plan '
        + 'bundles. That is why it lives here rather than under Spaces.',
    },
    {
      id: 'open-form',
      kind: 'act',
      route: MEETING_ROOMS,
      target: ANCHOR.meetingRoomCreate,
      placement: 'bottom',
      title: 'Add a room',
      body: 'Start with the room people ask for most. You can add the rest later.',
      waitingLabel: 'Click Create Meeting Room.',
      advanceOn: [{ type: 'click', target: ANCHOR.meetingRoomCreate }],
      doItForMe: { type: 'click', target: ANCHOR.meetingRoomCreate },
      allowSkip: true,
    },
    {
      id: 'describe',
      kind: 'narrate',
      route: PATH_ADMIN.createMeetingRoom.root,
      target: CENTER,
      placement: 'center',
      title: 'Capacity, price, and who may book it',
      body:
        'Capacity is what members filter on, so keep it honest. The hourly rate is '
        + 'what a booking costs when nobody has credits left to spend, and credits '
        + 'are drawn down before money is ever charged. Amenities and photos are '
        + 'not decoration here, they are what makes somebody pick this room.',
    },
    {
      id: 'save',
      kind: 'await',
      route: PATH_ADMIN.createMeetingRoom.root,
      target: CENTER,
      placement: 'center',
      title: 'Create the room',
      body:
        'Booking rules like the smallest slot, how far ahead people may book, and '
        + 'whether a booking needs approval are all editable afterwards. Get the '
        + 'room in first, then tune the rules once you see how it gets used.',
      waitingLabel: 'Waiting for the room to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.meetingRoomCreated }],
      allowSkip: true,
    },
    {
      id: 'public-link',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: ANCHOR.meetingRoomPublicLink,
      placement: 'bottom',
      title: 'You can sell it to people who are not members',
      body:
        'This button gives you a public directory link for your rooms. Anyone with '
        + 'the link can see availability and book, which turns an idle board room '
        + 'into revenue on the days your members are not using it.',
    },
    {
      id: 'done',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: CENTER,
      placement: 'center',
      title: 'The room is live',
      body:
        'Members see it in their portal straight away, and the calendar tab shows '
        + 'every booking across your rooms. If you run a room display panel at the '
        + 'door, it picks this room up with no extra setup.',
    },
  ],
};

export const SETUP_BRANDING = {
  id: 'setup-branding',
  title: 'Make it look like your brand',
  summary:
    'Your logo and colours across the member portal, the app, invoices and '
    + 'every email that leaves the system.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 3,
  readinessKey: 'branding',
  keywords: [
    'branding', 'logo', 'colours', 'colors', 'theme', 'whitelabel',
    'white label', 'organization settings',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: BRANDING,
      target: CENTER,
      placement: 'center',
      title: 'This is org-wide, not per campus',
      body:
        'Your logo and colours belong to the organisation, so what you set here '
        + 'follows every campus you run. Members reach you at your own address, '
        + 'and what they see there should look like you rather than like us.',
    },
    {
      id: 'edit',
      kind: 'act',
      route: BRANDING,
      target: ANCHOR.settingsBrandingEdit,
      placement: 'left',
      title: 'Open it for editing',
      body: 'The page is read-only until you do. That is deliberate.',
      waitingLabel: 'Click the edit button.',
      advanceOn: [{ type: 'click', target: ANCHOR.settingsBrandingEdit }],
      doItForMe: { type: 'click', target: ANCHOR.settingsBrandingEdit },
      allowSkip: true,
    },
    {
      id: 'save',
      kind: 'await',
      route: BRANDING,
      target: CENTER,
      placement: 'center',
      title: 'Logo, then the two colours',
      body:
        'Upload a logo with a transparent background if you have one, since it sits '
        + 'on both light and dark surfaces. The primary colour drives buttons and '
        + 'highlights, the secondary one accents them. Charts, badges and the mobile '
        + 'app all derive from these two, so you are styling far more than this page. '
        + 'Save when it looks right.',
      waitingLabel: 'Waiting for the branding to be saved.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.brandingSaved }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: BRANDING,
      target: CENTER,
      placement: 'center',
      title: 'It is applied everywhere',
      body:
        'Reload once and this console picks it up too. Members see it on their next '
        + 'visit. Invoice letterheads are separate on purpose, because those belong '
        + 'to the legal entity that bills, and you set those on the invoice template.',
    },
  ],
};

export const SETUP_FLOOR_PLAN = {
  id: 'setup-floor-plan',
  title: 'Put your seats on a floor plan',
  summary:
    'Upload the layout of a floor and place your desks on it, so occupancy is '
    + 'something you look at rather than something you read off a table.',
  category: 'setup',
  level: 'advanced',
  permission: ['spaces.view', 'spaces.manage'],
  featureSlug: null,
  estMinutes: 8,
  readinessKey: 'floor_plan',
  keywords: [
    'floor plan', 'layout', 'map', 'seat map', 'desk map', 'live status',
    'visual', 'builder',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PATH_ADMIN.floorPlans,
      target: CENTER,
      placement: 'center',
      title: 'One plan per floor',
      body:
        'A floor plan is a picture of a floor with your desks positioned on it. '
        + 'It does not create seats and it does not change occupancy. It puts the '
        + 'seats you already have somewhere on a page, so a glance tells you what '
        + 'is free and where it is. Have the floor image handy before you start.',
    },
    {
      id: 'create',
      kind: 'act',
      route: PATH_ADMIN.floorPlans,
      target: ANCHOR.floorPlanCreate,
      placement: 'left',
      title: 'Start with one floor',
      body:
        'Each floor is listed separately. Do the busiest one first, and only do '
        + 'the others once you are happy with how this one turned out.',
      waitingLabel: 'Click Create Floor Plan.',
      advanceOn: [{ type: 'click', target: ANCHOR.floorPlanCreate }],
      doItForMe: { type: 'click', target: ANCHOR.floorPlanCreate },
      allowSkip: true,
    },
    {
      id: 'name-it',
      kind: 'await',
      route: PATH_ADMIN.floorPlans,
      target: CENTER,
      placement: 'center',
      title: 'Name it and open the builder',
      body:
        'The name is only for your team, so the default is usually fine. It starts '
        + 'as a draft, which means nobody else sees it while you work on it.',
      waitingLabel: 'Waiting for the floor plan to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.floorPlanCreated }],
      allowSkip: true,
    },
    {
      id: 'builder',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'Image first, then desks',
      body:
        'Upload the floor image, then drop desks onto it and attach each one to a '
        + 'real seat. A desk with no seat behind it is just a drawing, so it will '
        + 'never colour in. Work in one area at a time rather than trying to place '
        + 'the whole floor at once.',
    },
    {
      id: 'publish',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'Publish when it is right',
      body:
        'Publishing is what makes the plan appear on Live Status, where occupied, '
        + 'vacant and on-notice seats colour themselves from the same numbers the '
        + 'rest of the system uses. Until then it stays your draft. You can come '
        + 'back and move desks whenever the floor changes.',
    },
  ],
};

export const SETUP_PAYMENT_GATEWAY = {
  id: 'setup-payment-gateway',
  title: 'Connect a payment gateway',
  summary:
    'Let members pay an invoice online instead of you chasing a bank transfer '
    + 'and matching it by hand.',
  category: 'setup',
  level: 'advanced',
  permission: 'settings.manage',
  featureSlug: null,
  estMinutes: 6,
  readinessKey: 'payment_gateway',
  keywords: [
    'payment', 'gateway', 'razorpay', 'stripe', 'online payment', 'pay now',
    'collect', 'card',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PATH_ADMIN.settings.integrations,
      target: CENTER,
      placement: 'center',
      title: 'One account per legal entity',
      body:
        'A gateway account belongs to a billing centre, not to a campus, because '
        + 'the money has to land in the bank account of the entity that raised the '
        + 'invoice. If you bill under two companies, you connect two accounts. '
        + 'DeskOS picks the right one at checkout on its own.',
    },
    {
      id: 'connect',
      kind: 'act',
      route: PATH_ADMIN.settings.integrations,
      target: ANCHOR.integrationRazorpayConnect,
      placement: 'top',
      title: 'Connect Razorpay',
      body:
        'You will need the login for the Razorpay account itself. We never see or '
        + 'store those credentials, the connection is authorised on their side.',
      waitingLabel: 'Click Connect on the Razorpay card.',
      advanceOn: [{ type: 'click', target: ANCHOR.integrationRazorpayConnect }],
      doItForMe: { type: 'click', target: ANCHOR.integrationRazorpayConnect },
      allowSkip: true,
    },
    {
      id: 'authorize',
      kind: 'await',
      route: PATH_ADMIN.settings.integrations,
      target: CENTER,
      placement: 'center',
      title: 'Pick the entity, then authorise',
      body:
        'Choose the billing centre this account collects for, then authorise in '
        + 'the window that opens. If nothing appears, your browser blocked the '
        + 'popup. Allow it for this site and try again. Come back here and click '
        + 'Done once Razorpay says you are connected.',
      waitingLabel: 'Waiting for the gateway to be connected.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.paymentGatewayConnected }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: PATH_ADMIN.settings.integrations,
      target: CENTER,
      placement: 'center',
      title: 'Invoices can be paid online now',
      body:
        'Every invoice you send carries a pay link, and a successful payment marks '
        + 'itself paid without anyone touching it. Test mode is on to begin with, '
        + 'so run one real rupee through it before you switch to live. If you sell '
        + 'outside India, connect Stripe the same way for those entities.',
    },
  ],
};

export default [
  SETUP_CAMPUS_PROFILE,
  SETUP_WORKING_HOURS,
  SETUP_SPACES_SEATS,
  SETUP_PLANS,
  SETUP_BILLING_CENTER,
  SETUP_TAX,
  SETUP_INVOICE_NUMBERING,
  SETUP_INVOICE_TEMPLATE,
  SETUP_TEAM,
  SETUP_ROLES,
  SETUP_MEETING_ROOMS,
  SETUP_BRANDING,
  SETUP_FLOOR_PLAN,
  SETUP_PAYMENT_GATEWAY,
];
